import { UploadIntentResponseSchema } from '@ootopia/shared-contracts'
import { AppButton, StatusAlert } from '@ootopia/ui'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as React from 'react'
import { ScanQuotaError, submitScanCompletion, uploadScanImage } from '../lib/capture-submit'

export const Route = createFileRoute('/capture')({
  component: CapturePage,
  validateSearch: (search: Record<string, unknown>) => ({
    planId: (search.planId as string) || undefined,
    returnTo: (search.returnTo as string) || undefined,
  }),
})

type CaptureState = 'consent' | 'camera' | 'preview' | 'uploading' | 'success' | 'error'

function CapturePage() {
  const navigate = useNavigate()
  const search = Route.useSearch() as { planId?: string; returnTo?: string }
  const planId = search.planId
  const returnTo = search.returnTo
  const [state, setState] = React.useState<CaptureState>('consent')
  const [stream, setStream] = React.useState<MediaStream | null>(null)
  const [capturedImage, setCapturedImage] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [errorKind, setErrorKind] = React.useState<'generic' | 'quota'>('generic')
  const [scanId, setScanId] = React.useState<string | null>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  const consentMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/v1/skin-scans/upload-intents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) throw new Error('Failed to create upload intent')
      const json = await res.json()
      return UploadIntentResponseSchema.parse(json)
    },
    onSuccess: (data) => {
      setScanId(data.scanId)
      setState('camera')
      startCamera()
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to start')
      setState('error')
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (imageData: string) => {
      if (!scanId) throw new Error('No scan ID')

      // Convert data URL to Blob
      const res = await fetch(imageData)
      const blob = await res.blob()

      // Upload image bytes first
      await uploadScanImage(scanId, blob)

      // Then submit completion to queue AI job
      return submitScanCompletion(scanId)
    },
    onSuccess: () => {
      // Navigate to scan status page after successful upload + completion
      if (scanId) {
        window.localStorage.setItem('ootopia:last-uploaded-scan-id', scanId)
        if (returnTo?.startsWith('/')) {
          window.location.assign(returnTo)
          return
        }
        navigate({
          to: '/scan/$id',
          params: { id: scanId },
          search: { planId },
        })
      } else {
        setState('success')
      }
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setErrorKind(err instanceof ScanQuotaError ? 'quota' : 'generic')
      setState('error')
    },
  })

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      setStream(mediaStream)
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (_err) {
      setError('Camera access denied. Please allow camera permissions.')
      setState('error')
    }
  }

  const stopCamera = () => {
    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop()
      }
      setStream(null)
    }
  }

  const stopCameraRef = React.useRef(stopCamera)
  stopCameraRef.current = stopCamera

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0)
    const imageData = canvas.toDataURL('image/jpeg', 0.85)
    setCapturedImage(imageData)
    setState('preview')
    stopCamera()
  }

  const retakePhoto = () => {
    setCapturedImage(null)
    setState('camera')
    startCamera()
  }

  const submitPhoto = () => {
    if (!capturedImage) return
    setState('uploading')
    uploadMutation.mutate(capturedImage)
  }

  React.useEffect(() => {
    return () => {
      stopCameraRef.current()
    }
  }, [])

  const [isOnline, setIsOnline] = React.useState(navigator.onLine)

  React.useEffect(() => {
    const on = () => setIsOnline(true)
    const off = () => setIsOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  if (!isOnline && state !== 'success') {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface' },
      React.createElement(StatusAlert, {
        color: 'warning',
        title: "You're offline",
        children: 'Check your connection and try again.',
      } as never),
    )
  }

  if (state === 'consent') {
    return React.createElement(
      'div',
      {
        className: 'ootopia-pwa-page ootopia-pwa-capture-consent ootopia-pwa-surface',
      },
      React.createElement('img', {
        src: '/homepage/journey-skin-scan.png',
        alt: 'Synthetic skin scan preview',
        className: 'ootopia-pwa-capture-consent__media',
        loading: 'eager',
        decoding: 'async',
      }),
      React.createElement(
        'h1',
        { className: 'ootopia-editorial-heading' },
        'A calmer scan, made for your skin.',
      ),
      React.createElement(
        'p',
        { className: 'max-w-md text-center text-default-500' },
        'Take one clear frame in natural light. The image stays private, and you control deletion from privacy settings.',
      ),
      React.createElement(
        'div',
        { className: 'ootopia-pwa-capture-consent__actions' },
        React.createElement(
          AppButton,
          {
            color: 'primary',
            fullWidth: true,
            onPress: () => consentMutation.mutate(),
            isLoading: consentMutation.isPending,
          } as never,
          'I consent, open camera',
        ),
        React.createElement(
          AppButton,
          {
            variant: 'light',
            fullWidth: true,
            onPress: () => window.history.back(),
          } as never,
          'Not now',
        ),
      ),
    )
  }

  if (state === 'camera') {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-camera' },
      React.createElement(
        'div',
        { className: 'ootopia-pwa-camera__frame' },
        React.createElement('video', {
          ref: videoRef,
          autoPlay: true,
          playsInline: true,
          muted: true,
          className: 'w-full h-full object-cover',
        }),
        React.createElement('canvas', { ref: canvasRef, className: 'hidden' }),
      ),
      React.createElement(
        'p',
        { className: 'text-sm text-default-500 text-center px-4' },
        'Position your face in the center. Ensure good lighting.',
      ),
      React.createElement(
        'div',
        { className: 'ootopia-pwa-sticky-action w-full max-w-md' },
        React.createElement(
          AppButton,
          {
            color: 'primary',
            onPress: capturePhoto,
          } as never,
          'Capture',
        ),
        React.createElement(
          AppButton,
          {
            variant: 'light',
            onPress: () => {
              stopCamera()
              setState('consent')
            },
          } as never,
          'Back',
        ),
      ),
    )
  }

  if (state === 'preview' && capturedImage) {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-camera' },
      React.createElement(
        'div',
        { className: 'ootopia-pwa-camera__frame' },
        React.createElement('img', {
          src: capturedImage,
          alt: 'Captured photo',
          className: 'w-full h-full object-cover',
        }),
      ),
      React.createElement(
        'div',
        { className: 'ootopia-pwa-sticky-action w-full max-w-md' },
        React.createElement(
          AppButton,
          {
            color: 'primary',
            onPress: submitPhoto,
          } as never,
          'Use This Photo',
        ),
        React.createElement(
          AppButton,
          {
            variant: 'light',
            onPress: retakePhoto,
          } as never,
          'Retake',
        ),
      ),
    )
  }

  if (state === 'uploading') {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface' },
      React.createElement('div', {
        className:
          'animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent',
      }),
      React.createElement('p', { className: 'text-default-500' }, 'Processing your photo...'),
    )
  }

  if (state === 'success') {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface' },
      React.createElement(StatusAlert, {
        color: 'success',
        title: 'Photo captured!',
        children: 'Your skin analysis is being processed.',
      } as never),
      React.createElement(
        AppButton,
        {
          color: 'primary',
          onPress: () => window.history.back(),
        } as never,
        'Done',
      ),
    )
  }

  if (state === 'error') {
    return React.createElement(
      'div',
      { className: 'ootopia-pwa-page ootopia-pwa-confirm ootopia-pwa-surface' },
      React.createElement(StatusAlert, {
        color: errorKind === 'quota' ? 'warning' : 'danger',
        title: errorKind === 'quota' ? 'Daily scan limit reached' : 'Something went wrong',
        children: error || 'An unexpected error occurred.',
      } as never),
      React.createElement(
        AppButton,
        {
          color: errorKind === 'quota' ? 'secondary' : 'primary',
          onPress: () => {
            if (errorKind === 'quota') {
              navigate({ to: '/looks' })
            } else {
              setError(null)
              setErrorKind('generic')
              setState('consent')
            }
          },
        } as never,
        errorKind === 'quota' ? 'Choose a look' : 'Try Again',
      ),
    )
  }

  return null
}
